import { useEffect, useMemo, useState } from 'react';
import './DashboardApp.css';

const WINDOWS = [
  { key: '1', label: '1 hour' },
  { key: '24', label: '24 hours' },
];

function number(value) {
  return Number(value || 0).toLocaleString();
}

function arrayOrEmpty(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeWindow(windowData = {}) {
  return {
    ...windowData,
    request_minutes: windowData.request_minutes ?? 0,
    requests_by_method: arrayOrEmpty(windowData.requests_by_method),
    rate_limits_by_category: arrayOrEmpty(windowData.rate_limits_by_category),
    retry_after: {
      riot_seconds: [],
      effective_seconds: [],
      missing: 0,
      unusable: 0,
      ...(windowData.retry_after || {}),
    },
  };
}

function normalizeDashboardPayload(rawPayload = {}) {
  const queue = rawPayload.queue || {};

  return {
    ...rawPayload,
    windows: {
      '1': normalizeWindow(rawPayload.windows?.['1']),
      '24': normalizeWindow(rawPayload.windows?.['24']),
    },
    routing_views: {
      platform: arrayOrEmpty(rawPayload.routing_views?.platform),
      regional: arrayOrEmpty(rawPayload.routing_views?.regional),
    },
    routing_groups: arrayOrEmpty(rawPayload.routing_groups),
    limit_usage: arrayOrEmpty(rawPayload.limit_usage),
    recent_rate_limits: arrayOrEmpty(rawPayload.recent_rate_limits),
    queue: {
      ...queue,
      available: queue.available ?? false,
      status_counts: arrayOrEmpty(queue.status_counts),
      recent_errors: arrayOrEmpty(queue.recent_errors),
    },
    configuration: rawPayload.configuration || null,
  };
}

function rateLimitScope(row) {
  const scope = [row.service, row.method].filter(Boolean).join(' / ');
  return `${row.routing} / ${scope || 'application'}`;
}

function limiterMethodName(method) {
  const value = String(method || '').trim();
  if (!value) return 'unknown-method';
  return value.startsWith('/') ? value.slice(1).replaceAll('/', '-') : value;
}

function limiterMethodLabel(routing, method) {
  return `${routing} / ${method.service} / ${limiterMethodName(method.method)}`;
}

function LimiterMethodCode({ routing, method }) {
  return <code className="dashboard-method-code">{limiterMethodLabel(routing, method)}</code>;
}

function retryAfterSummary(fields = {}) {
  const safeFields = fields || {};
  const observations = Object.entries(safeFields)
    .filter(([field, count]) => field.startsWith('riot_retry_after_seconds:') && count > 0)
    .map(([field]) => field.split(':').pop());
  if (observations.length) return `Riot requested ${observations.join(' or ')} seconds`;
  if (safeFields.riot_retry_after_missing) return 'Riot header missing';
  if (safeFields.riot_retry_after_unusable) return 'Riot header unusable';
  return 'not observed';
}

function windowLabel(seconds) {
  if (seconds >= 60 && seconds % 60 === 0) {
    const minutes = seconds / 60;
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
}

function applicationLimitCards(route) {
  return arrayOrEmpty(route.application).map((window) => (
    <article className="dashboard-limit-card" key={`${route.routing}-application-card-${window.window}`}>
      <div className="dashboard-limit-card-heading">
        <span>{windowLabel(window.window_seconds)} window</span>
        <span>{window.ttl_seconds === null ? 'inactive' : `resets in ${windowLabel(window.ttl_seconds)}`}</span>
      </div>
      <strong className="dashboard-limit-card-value">
        {number(window.current)} <span>/ {number(window.limit)}</span>
      </strong>
      <Utilization value={window.utilization_percent} />
      <span className="dashboard-limit-card-caption">admitted requests in this routing-value budget</span>
    </article>
  ));
}

function methodUtilization(method) {
  return Math.max(
    ...(arrayOrEmpty(method.windows).map((window) => Number(window.utilization_percent) || 0)),
    0,
  );
}

function serviceUtilization(methods) {
  return Math.max(...methods.map(methodUtilization), 0);
}

function methodLimitRows(route) {
  const serviceGroups = new Map();
  arrayOrEmpty(route.methods).forEach((method) => {
    const methods = serviceGroups.get(method.service) || [];
    methods.push(method);
    serviceGroups.set(method.service, methods);
  });

  return [...serviceGroups.entries()]
    .sort(([, left], [, right]) => (
      serviceUtilization(right) - serviceUtilization(left)
      || left[0].service.localeCompare(right[0].service)
    ))
    .flatMap(([service, methods]) => [
      <tr key={`${route.routing}-${service}-group`} className="dashboard-method-service">
        <td colSpan="5">{service}</td>
      </tr>,
      ...methods
        .sort((left, right) => (
          methodUtilization(right) - methodUtilization(left)
          || limiterMethodLabel(route.routing, left).localeCompare(limiterMethodLabel(route.routing, right))
        ))
        .flatMap((method) => {
          const windows = [...arrayOrEmpty(method.windows)].sort(
            (left, right) => left.window_seconds - right.window_seconds,
          );
          return windows.map((window, index) => (
            <tr
              key={`${route.routing}-${method.service}-${method.method}-${window.window}`}
              className={index === 0 ? 'dashboard-method-start' : ''}
            >
              {index === 0 && (
                <td className="dashboard-method-label" rowSpan={windows.length}>
                  <LimiterMethodCode routing={route.routing} method={method} />
                </td>
              )}
              <td>{windowLabel(window.window_seconds)}</td>
              <td>{number(window.current)} / {number(window.limit)}</td>
              <td><Utilization value={window.utilization_percent} /></td>
              <td>{window.ttl_seconds === null ? 'inactive' : windowLabel(window.ttl_seconds)}</td>
            </tr>
          ));
        }),
    ]);
}

function timeAgo(bucket) {
  const timestamp = Date.parse(bucket);
  if (Number.isNaN(timestamp)) return '';

  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function Stat({ label, value, tone = '' }) {
  return (
    <div className={`dashboard-stat ${tone}`}>
      <strong>{number(value)}</strong>
      <span>{label}</span>
    </div>
  );
}

function StatusChip({ label, tone = '' }) {
  return <span className={`dashboard-status-chip ${tone}`}>{label}</span>;
}

function Utilization({ value }) {
  const percentage = Math.max(0, Number(value) || 0);
  const tone = percentage >= 80 ? 'high' : percentage >= 50 ? 'medium' : 'low';

  return (
    <span className={`dashboard-utilization ${tone}`}>
      <span className="dashboard-utilization-track" aria-hidden="true">
        <span className="dashboard-utilization-fill" style={{ width: `${Math.min(100, percentage)}%` }} />
      </span>
      <strong>{percentage}%</strong>
    </span>
  );
}

function DataTable({ headers, rows, empty, className = '' }) {
  if (!rows.length) {
    return <p className="dashboard-empty">{empty}</p>;
  }

  return (
    <div className="dashboard-table-wrap">
      <table className={`dashboard-table ${className}`}>
        <thead>
          <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

function DashboardApp() {
  const [payload, setPayload] = useState(null);
  const [selectedWindow, setSelectedWindow] = useState('1');
  const [selectedRoutingValue, setSelectedRoutingValue] = useState('na1');
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    document.title = 'WR.GG / operations';
    document.body.classList.add('dashboard-page');

    return () => document.body.classList.remove('dashboard-page');
  }, []);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const params = new URLSearchParams({
          window_hours: '24',
          routing_value: selectedRoutingValue,
        });
        const response = await fetch(`/dashboard/data?${params.toString()}`, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const nextPayload = await response.json();
        if (active) {
          setPayload(normalizeDashboardPayload(nextPayload));
          setError('');
          setUpdatedAt(new Date(nextPayload.generated_at));
        }
      } catch (loadError) {
        if (active) {
          setError(`Dashboard data unavailable: ${loadError.message}`);
        }
      }
    }

    load();
    const interval = window.setInterval(load, 30000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [selectedRoutingValue]);

  function selectRoutingScope(selection) {
    setSelectedRoutingValue(selection);
  }

  const windowData = payload?.windows?.[selectedWindow];
  const averageRequestsPerActiveMinute = windowData?.request_minutes
    ? Math.round(windowData.requests_total / windowData.request_minutes)
    : 0;
  const maxMethod = useMemo(
    () => Math.max(...(windowData?.requests_by_method || []).map((row) => row.count), 1),
    [windowData],
  );
  const configurationNotes = useMemo(() => {
    if (!payload?.configuration) return [];
    const notes = [];
    if (!payload.configuration.error_metrics_enabled) notes.push('error metrics are disabled');
    if (!payload.configuration.request_metrics_enabled) notes.push('request metrics are disabled');
    return notes;
  }, [payload]);
  const queueRows = payload?.queue?.available
    ? [
      ...payload.queue.status_counts.map((row) => {
        const toneByStatus = {
          queued: 'active',
          processing: 'active',
          retry: 'warning',
          done: 'success',
          failed: 'error',
          skipped: 'warning',
        };
        const tone = toneByStatus[row.label] || '';
        return (
          <tr key={`status-${row.label}`}>
            <td><StatusChip label={row.label} tone={tone} /></td>
            <td>{number(row.count)}</td>
          </tr>
        );
      }),
    ]
    : [];
  const retryAfterRows = [
    ...arrayOrEmpty(windowData?.retry_after?.riot_seconds).map((row) => (
      <tr key={`riot-${row.seconds}`}><td>Riot requested wait: {row.seconds} seconds</td><td>{number(row.count)}</td></tr>
    )),
    ...(windowData?.retry_after?.missing > 0
      ? [<tr key="missing"><td>Riot Retry-After missing</td><td>{number(windowData.retry_after.missing)}</td></tr>]
      : []),
    ...(windowData?.retry_after?.unusable > 0
      ? [<tr key="unusable"><td>Riot Retry-After unusable</td><td>{number(windowData.retry_after.unusable)}</td></tr>]
      : []),
  ];

  return (
    <main className="dashboard-shell">
      <header className="dashboard-header">
        <div>
          <p className="dashboard-kicker">WR.GG / internal operations</p>
          <h1>Traffic and queue health</h1>
          <p className="dashboard-subhead">
            Private operating view of requests admitted to Riot, limiter decisions,
            configured capacity, and match work waiting in PostgreSQL.
          </p>
        </div>
        <div className="dashboard-toolbar" aria-label="Dashboard time window">
          <div className="dashboard-window-buttons">
            {WINDOWS.map((window) => (
              <button
                key={window.key}
                type="button"
                aria-pressed={selectedWindow === window.key}
                onClick={() => setSelectedWindow(window.key)}
              >
                {window.label}
              </button>
            ))}
          </div>
          <div className="dashboard-filter">
            <span className="dashboard-filter-label">Traffic scope</span>
            <div className="dashboard-filter-section">
              <span className="dashboard-filter-section-label">Platform /<br />server</span>
              <span className="dashboard-routing-buttons">
                {(payload?.routing_views?.platform || []).map((routingValue) => (
                  <button
                    key={routingValue}
                    type="button"
                    aria-pressed={selectedRoutingValue === routingValue}
                    onClick={() => selectRoutingScope(routingValue)}
                    title={`Only ${routingValue.toUpperCase()} traffic and limiter counters`}
                  >
                    {routingValue.toUpperCase()}
                  </button>
                ))}
              </span>
            </div>
            <div className="dashboard-filter-section">
              <span className="dashboard-filter-section-label">Regional views</span>
              <span className="dashboard-routing-buttons">
                {(payload?.routing_groups || []).map((group) => (
                  <button
                    key={group.key}
                    type="button"
                    aria-pressed={selectedRoutingValue === group.key}
                    onClick={() => selectRoutingScope(group.key)}
                    title={group.hint}
                  >
                    {group.label}
                  </button>
                ))}
              </span>
            </div>
            <div className="dashboard-filter-section">
              <span className="dashboard-filter-section-label">Global view</span>
              <span className="dashboard-routing-buttons">
                <button
                  type="button"
                  aria-pressed={selectedRoutingValue === 'all'}
                  onClick={() => selectRoutingScope('all')}
                >
                  Global
                </button>
              </span>
            </div>
          </div>
          <span className="dashboard-updated">
            {updatedAt ? `Updated ${updatedAt.toLocaleTimeString()}` : 'Loading...'}
          </span>
        </div>
      </header>

      {error && <div className="dashboard-notice dashboard-notice-error">{error}</div>}
      {configurationNotes.length > 0 && (
        <div className="dashboard-notice">
          Configuration note: {configurationNotes.join(' and ')}.
        </div>
      )}

      {!windowData ? (
        <div className="dashboard-loading">Loading dashboard data...</div>
      ) : (
        <section className="dashboard-grid">
          <article className="dashboard-panel dashboard-panel-requests">
            <div className="dashboard-panel-heading">
              <h2>Admitted outbound requests</h2>
              <span>New Destiny request metrics</span>
            </div>
            <p className="dashboard-measure">Requests admitted by New Destiny immediately before WR.GG sends them to Riot. This is outbound traffic volume, not successful-response volume.</p>
            <div className="dashboard-stats">
              <Stat label={`requests admitted / ${selectedWindow === '1' ? '1h' : '24h'}`} value={windowData.requests_total} tone="good" />
              <Stat label="methods observed" value={windowData.requests_by_method.length} />
              <Stat label="request-metric keys captured" value={windowData.request_buckets} />
            </div>
            <p className="dashboard-help">Each captured Redis key aggregates one method and routing value during one one-minute window. This count is keys, not requests or elapsed minutes. The average below uses distinct active minutes to estimate traffic pace.</p>
            <div className="dashboard-pace">
              <strong>{number(averageRequestsPerActiveMinute)}</strong>
              <span>average admitted requests per active minute</span>
            </div>
            <DataTable
              headers={['Method / routing value', 'Requests']}
              rows={windowData.requests_by_method.slice(0, 20).map((row) => (
                <tr key={row.label}>
                  <td>
                    <span className="dashboard-bar" style={{ width: `${Math.max(2, Math.round(row.count / maxMethod * 120))}px` }} />
                    {row.label}
                  </td>
                  <td>{number(row.count)}</td>
                </tr>
              ))}
              empty="No admitted request metrics in this window. Enable request metrics for traffic analysis."
            />
          </article>

          <article className="dashboard-panel dashboard-panel-decisions">
            <div className="dashboard-panel-heading">
              <h2>Rate-limited events</h2>
              <span>raised exceptions</span>
            </div>
            <p className="dashboard-measure">Each event is a raised New Destiny rate-limit exception. Internal means New Destiny stopped the request before Riot; external means Riot returned a throttle.</p>
            <div className="dashboard-stats">
              <Stat label="raised exceptions" value={windowData.rate_limit_total} tone={windowData.rate_limit_total ? 'warn' : 'good'} />
              <Stat label="internal" value={windowData.internal_rate_limits} tone="good" />
              <Stat label="external" value={windowData.external_rate_limits} tone={windowData.external_rate_limits ? 'warn' : 'good'} />
            </div>
            <div className="dashboard-decision-key" aria-label="Rate-limit decision meanings">
              <span><StatusChip label="internal" tone="active" /> New Destiny stopped it before Riot.</span>
              <span><StatusChip label="external" tone="error" /> Riot returned the throttle.</span>
            </div>
            <DataTable
              headers={['Category', 'Count']}
              rows={windowData.rate_limits_by_category.map((row) => (
                <tr key={row.label}><td>{row.label}</td><td>{number(row.count)}</td></tr>
              ))}
              empty="No raised rate-limit exceptions in this window."
            />
            <div className="dashboard-subpanel">
              <div className="dashboard-panel-heading">
                <h3>Throttle timing</h3>
                <span>Riot 429 metadata</span>
              </div>
              <p className="dashboard-help">Riot requested wait is the usable <code>Retry-After</code> value returned with a 429. Missing and unusable headers are counted separately.</p>
              <DataTable
                headers={['Timing signal', 'Events']}
                rows={retryAfterRows}
                empty="No Riot throttle timing was observed in this window."
              />
            </div>
          </article>

          <article className="dashboard-panel dashboard-panel-wide dashboard-panel-timeline">
            <div className="dashboard-panel-heading">
              <h2>Rate-limit event timeline</h2>
              <span>minute aggregates, newest first</span>
            </div>
            <p className="dashboard-measure">Each row aggregates raised exceptions for one UTC minute and one limit category. It shows when and where the event occurred, plus the retry duration Riot supplied when available.</p>
            <div className="dashboard-timeline-scroll">
              <DataTable
                headers={['UTC minute / age', 'Category', 'Routing / scope', 'Riot wait', 'Count']}
                rows={payload.recent_rate_limits.map((row, index) => (
                  <tr key={`${row.bucket}-${row.type}-${row.routing}-${index}`}>
                    <td>
                      {row.bucket}
                      <span className="dashboard-time-ago">{timeAgo(row.bucket)}</span>
                    </td>
                    <td>
                      {row.type} / <span className={row.enforcement === 'external' ? 'dashboard-external' : 'dashboard-internal'}>{row.enforcement}</span>
                    </td>
                    <td>{rateLimitScope(row)}</td>
                    <td>{retryAfterSummary(row.retry_after_fields)}</td>
                    <td>{number(row.count)}</td>
                  </tr>
                ))}
                empty="No rate-limit events have been recorded yet."
              />
            </div>
          </article>

          <article className="dashboard-panel dashboard-panel-wide dashboard-panel-usage">
            <div className="dashboard-panel-heading">
              <h2>Live limiter usage</h2>
              <span>current Redis counters</span>
            </div>
            <p className="dashboard-help">
              Application limiters are shared budgets for one routing value, not one UI region: <code>na1</code> and <code>americas</code> have separate counters and cards. Selecting a platform/server shows that platform plus its associated regional router, not sibling platforms; regional buttons intentionally show the full group. OCE/SEA platforms can show both <code>asia</code> and <code>sea</code>: most regional endpoints use <code>asia</code>, while Match-V5 uses <code>sea</code>. Method limiters are per-endpoint budgets. Each current / limit value compares the live Redis counter with its configured ceiling; inactive means that Redis has no current key. These ceilings come from the active New Destiny configuration, not API-key introspection: custom production limits and windows are shown when configured, while non-production mode shows its non-production ceilings.
            </p>
            <section className="dashboard-limit-group dashboard-limit-group-top">
              <div className="dashboard-limit-group-heading">
                <h3>Application rate limiters</h3>
                <span>top-level budgets by routing value</span>
              </div>
              {(payload.limit_usage || []).map((route) => (
                <div className="dashboard-route-limiter" key={`application-${route.routing}`}>
                  <div className="dashboard-route-heading">
                    <h4>{route.routing} <span className="dashboard-route-value-label">- routing value</span></h4>
                    <span>{payload.routing_views?.regional?.includes(route.routing) ? 'regional router' : 'platform / server'}</span>
                  </div>
                  {route.application?.length ? (
                    <div className="dashboard-application-limits">
                      {applicationLimitCards(route)}
                    </div>
                  ) : (
                    <p className="dashboard-empty">No application limits are configured for this routing value.</p>
                  )}
                </div>
              ))}
            </section>
            <section className="dashboard-limit-group">
              <div className="dashboard-limit-group-heading">
                <h3>Method rate limiters</h3>
                <span>grouped by service, then method</span>
              </div>
              {(payload.limit_usage || []).map((route) => (
                <div className="dashboard-route-limiter" key={`method-${route.routing}`}>
                  <div className="dashboard-route-heading">
                    <h4>{route.routing} <span className="dashboard-route-value-label">- routing value</span></h4>
                    <span>highest utilization first</span>
                  </div>
                  <DataTable
                    headers={['Routing / service / method', 'Window', 'Current / limit', 'Utilization', 'Reset']}
                    rows={methodLimitRows(route)}
                    className="dashboard-limit-table"
                    empty="No method limits are configured for this routing value."
                  />
                </div>
              ))}
            </section>
          </article>

          <article className="dashboard-panel dashboard-panel-wide dashboard-panel-queue">
            <div className="dashboard-panel-heading">
              <h2>Match queue drain status</h2>
              <span>{payload.queue.available ? 'current PostgreSQL rows' : 'database unavailable'}</span>
            </div>
            <p className="dashboard-measure">Every status remains visible so a zero count is meaningful. Counts fall as terminal rows are cleaned up.</p>
            <DataTable
              headers={['Status', 'Rows']}
              rows={queueRows}
              empty={payload.queue.available ? 'The queue is empty.' : 'Queue data is unavailable.'}
            />
          </article>

          <article className="dashboard-panel dashboard-panel-wide dashboard-panel-errors">
            <div className="dashboard-panel-heading">
              <h2>Recent queue errors</h2>
              <span>newest 50 queue rows with errors</span>
            </div>
            <p className="dashboard-measure">This is up to 50 queue rows ordered by their most recent error timestamp. Each row stores its latest error, not a complete error-event history. Resolved means the same queue row later reached done.</p>
            <DataTable
              headers={['When / match', 'Error type', 'Message', 'Current state']}
              rows={(payload.queue.recent_errors || []).map((row) => (
                <tr key={`${row.id}-${row.occurred_at}`}>
                  <td>
                    <strong>{row.platform} / {row.match_id}</strong>
                    <span className="dashboard-time-ago">{timeAgo(row.occurred_at)}{row.occurred_at ? ` (${new Date(row.occurred_at).toLocaleString()})` : ''}</span>
                  </td>
                  <td>{row.error_type}</td>
                  <td className="dashboard-error-message">{row.message}</td>
                  <td><StatusChip label={row.resolved ? 'resolved' : row.status} tone={row.resolved ? 'success' : row.status === 'failed' ? 'error' : 'active'} /></td>
                </tr>
              ))}
              empty="No queue errors are recorded in the retained queue rows."
            />
          </article>

        </section>
      )}
    </main>
  );
}

export default DashboardApp;
